import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { getSocialFeeDisplayRate } from '../enums/socialFeeRateMapping.enum';
import { formatAmountWithLocale } from '../helper/common.helper';
import './FreelancerCalculationModal.scss';

// Safe to call repeatedly — registers the pieces we need for the chart tab
ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, ChartTooltip, ChartLegend);

// Violet-family palette for chart slices/bars (matches the modal theme)
const CHART_PALETTE = [
  '#7c3aed', '#a855f7', '#c084fc', '#8b5cf6', '#6366f1',
  '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6',
];

interface FreelancerCalculationModalProps {
  isOpen: boolean;
  onClose: () => void;
  calculationData: any;
  freelancerId: string;
  currency?: string;
}

export const FreelancerCalculationModal: React.FC<FreelancerCalculationModalProps> = ({
  isOpen,
  onClose,
  calculationData,
  freelancerId,
  currency = 'SEK'
}) => {
  const { t, i18n } = useTranslation();

  // Breakdown (list) vs Chart view — hooks must run before any early return
  const [activeTab, setActiveTab] = useState<'breakdown' | 'chart'>('breakdown');
  useEffect(() => {
    if (isOpen) setActiveTab('breakdown'); // always reopen on the list view
  }, [isOpen]);

  // Custom scroll controls (scrollbar hidden → up/down buttons instead)
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollState = React.useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 8);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 8);
  }, []);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    updateScrollState();
    // Recompute after the open animation / content settles
    const timers = [80, 320].map((ms) => setTimeout(updateScrollState, ms));
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      timers.forEach(clearTimeout);
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [isOpen, activeTab, updateScrollState]);

  const scrollByViewport = (direction: 1 | -1) => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollBy({ top: direction * el.clientHeight * 0.8, behavior: 'smooth' });
  };

  // ── Sliding pill-tab indicator ─────────────────────────────────────────────
  const tabsRef = React.useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const [indicatorReady, setIndicatorReady] = useState(false);

  const measureIndicator = React.useCallback(() => {
    const container = tabsRef.current;
    if (!container) return;
    const activeBtn = container.querySelector<HTMLElement>('.fcm-tab.active');
    if (activeBtn) setIndicator({ left: activeBtn.offsetLeft, width: activeBtn.offsetWidth });
  }, []);

  // Position BEFORE paint on every tab change → no flash, no slide-in
  useLayoutEffect(() => {
    measureIndicator();
  }, [activeTab, isOpen, measureIndicator]);

  // Enable the transition only one frame after opening, so the pill doesn't
  // slide in from the left on first render — it only animates on real switches.
  // The modal body mounts during an enter transition, so re-measure a couple of
  // times to be sure the indicator gets a real width (until then the active tab
  // keeps its own gradient fallback and is never invisible).
  useEffect(() => {
    if (!isOpen) {
      setIndicatorReady(false);
      return;
    }
    measureIndicator();
    const raf = requestAnimationFrame(() => {
      measureIndicator();
      setIndicatorReady(true);
    });
    const timers = [80, 260, 500].map((ms) => setTimeout(measureIndicator, ms));
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [isOpen, measureIndicator]);

  // Keep the indicator aligned on resize (also covers mobile horizontal scroll layout)
  useEffect(() => {
    window.addEventListener('resize', measureIndicator);
    return () => window.removeEventListener('resize', measureIndicator);
  }, [measureIndicator]);

  // Get freelancer-specific data from API response
  const data = calculationData?.response;
  const freelancerData = data?.freelancersData?.find(
    (fd: any) => fd.createdBy?.toString() === freelancerId?.toString()
  );

  // If no freelancer data found, don't render
  if (!freelancerData) {
    return null;
  }

  // Determine if we should use SEK values (when currency is not SEK)
  const isForeignCurrency = data?.totalCalculation?.currencyName && data.totalCalculation.currencyName !== 'SEK';
  
  // Format currency - always show SEK when foreign currency
  const formatCurrency = (amount: number) => {
    if (amount === 0 || amount === null || amount === undefined) {
      return formatAmountWithLocale(0, 'SEK', i18n.language);
    }
    return `${amount < 0 ? '-' : ''}${formatAmountWithLocale(amount, 'SEK', i18n.language)}`;
  };
  
  // Format original currency (for Invoice Amount and VAT fields)
  const formatCurrencyOriginal = (amount: number) => {
    if (amount === 0 || amount === null || amount === undefined) {
      return formatAmountWithLocale(0, currency, i18n.language);
    }
    return `${amount < 0 ? '-' : ''}${formatAmountWithLocale(amount, currency, i18n.language)}`;
  };

  // Negative amounts (payout too low to cover fees/tax) are shown in red
  const valueCls = (amount: number) => (Number(amount) < 0 ? 'item-value is-negative' : 'item-value');
  
  // Helper to get SEK amount
  const getAmount = (directAmount: number, sekAmount: number) => {
    return isForeignCurrency ? sekAmount : directAmount;
  };

  const calcData = freelancerData;

  // ── Chart data (mirrors the exact same values shown in the breakdown list) ──
  const money = (direct: number, sek: number) => getAmount(direct, sek);

  // "Where the invoiced money goes" — composition for the doughnut
  const composition = [
    { label: t('NET_SALARY') || 'Net Salary', value: money(calcData.net, calcData.netInSek || calcData.net) },
    { label: t('INCOME_TAX') || 'Income Tax', value: money(calcData.tax, calcData.taxInSek || calcData.tax) },
    { label: t('HOLIDAY_PAY') || 'Holiday Pay', value: money(calcData.holidayPayAmount, calcData.holidayPayAmountInSek || calcData.holidayPayAmount) },
    { label: t('SOCIAL_FEE') || 'Social Fee', value: money(calcData.socialFee, calcData.socialFeeInSek || calcData.socialFee) },
    { label: t('PENSION_SOCIAL_FEE') || 'Pension Social Fee', value: money(calcData.pensionSocialFee, calcData.pensionSocialFeeInSek || calcData.pensionSocialFee) },
    { label: t('PENSION') || 'Pension', value: money(calcData.pension, calcData.pensionInSek || calcData.pension) },
    { label: t('INVOZIO_FEE') || 'Invozio Fee', value: money(calcData.fee, calcData.feeInSEK || calcData.fee) },
    { label: t('EXPRESS_FEE') || 'Express Fee', value: money(calcData.expressFee, calcData.expressFeeInSek || calcData.expressFee) },
  ].filter((x) => typeof x.value === 'number' && !isNaN(x.value) && x.value > 0);

  const doughnutData = {
    labels: composition.map((x) => x.label),
    datasets: [
      {
        data: composition.map((x) => x.value),
        backgroundColor: composition.map((_, i) => CHART_PALETTE[i % CHART_PALETTE.length]),
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  // Every line item (the full breakdown) for the bar chart
  const barSource: Array<{ label: string; value: number }> = [];
  const pushBar = (label: string, value: number) => {
    if (typeof value === 'number' && !isNaN(value) && value !== 0) barSource.push({ label, value });
  };
  pushBar(t('INVOICE_AMOUNT_INCLUDING_VAT') || 'Invoice Amount inc. VAT', calcData.amountIncludingVat);
  if (calcData.rutDeductionAmount > 0) pushBar(t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction', -money(calcData.rutDeductionAmount, calcData.rutDeductionAmount));
  if (calcData.rootDeductionAmount > 0) pushBar(t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction', -money(calcData.rootDeductionAmount, calcData.rootDeductionAmount));
  if (calcData.vat > 0) pushBar(`${t('VAT') || 'VAT'} (${calcData.vatPercentage}%)`, calcData.vat);
  pushBar(t('INVOICE_AMOUNT_EXCLUDING_VAT') || 'Invoice amount ex. VAT', money(calcData.amount, calcData.amountInSek || calcData.amount));
  if (calcData.fee > 0) pushBar(t('INVOZIO_FEE') || 'Invozio Fee', money(calcData.fee, calcData.feeInSEK || calcData.fee));
  if (calcData.socialFee > 0) pushBar(t('SOCIAL_FEE') || 'Social Fee', money(calcData.socialFee, calcData.socialFeeInSek || calcData.socialFee));
  if (calcData.pensionSocialFee > 0) pushBar(t('PENSION_SOCIAL_FEE') || 'Pension Social Fee', money(calcData.pensionSocialFee, calcData.pensionSocialFeeInSek || calcData.pensionSocialFee));
  if (calcData.pension > 0) pushBar(t('PENSION') || 'Pension', money(calcData.pension, calcData.pensionInSek || calcData.pension));
  pushBar(t('GROSS_SALARY_INCLUDING_HOLIDAY_PAY') || 'Gross salary', money(calcData.gross, calcData.grossInSek || calcData.gross));
  if (calcData.tax > 0) pushBar(t('INCOME_TAX') || 'Income Tax', money(calcData.tax, calcData.taxInSek || calcData.tax));
  if (calcData.holidayPayAmount > 0) pushBar(t('HOLIDAY_PAY') || 'Holiday Pay', money(calcData.holidayPayAmount, calcData.holidayPayAmountInSek || calcData.holidayPayAmount));
  if (calcData.expressFee > 0) pushBar(t('EXPRESS_FEE') || 'Express Fee', money(calcData.expressFee, calcData.expressFeeInSek || calcData.expressFee));
  pushBar(t('NET_SALARY') || 'Net Salary', money(calcData.net, calcData.netInSek || calcData.net));

  const barData = {
    labels: barSource.map((x) => x.label),
    datasets: [
      {
        label: t('AMOUNT') || 'Amount',
        data: barSource.map((x) => x.value),
        backgroundColor: barSource.map((x) => (x.value < 0 ? '#ef4444' : '#7c3aed')),
        borderRadius: 6,
        maxBarThickness: 22,
      },
    ],
  };

  const chartTooltip = {
    callbacks: {
      label: (ctx: any) => {
        const v = ctx.parsed?.y ?? ctx.parsed?.x ?? ctx.parsed ?? ctx.raw;
        return ` ${formatCurrency(Math.abs(Number(v)) * (Number(v) < 0 ? -1 : 1))}`;
      },
    },
  };

  const doughnutOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '64%',
    plugins: { legend: { display: false }, tooltip: chartTooltip },
    animation: { animateScale: true, animateRotate: true, duration: 700 },
  };

  const barOptions: any = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: chartTooltip },
    animation: { duration: 700, easing: 'easeOutQuart' },
    scales: {
      x: { grid: { color: 'rgba(124,58,237,0.08)' }, ticks: { color: '#6b6480', font: { size: 11 } } },
      y: { grid: { display: false }, ticks: { color: '#1a1330', font: { size: 11, weight: '600' } } },
    },
  };

  return (
    <Modal
      show={isOpen} 
      onHide={onClose} 
      size="lg" 
      centered 
      className="freelancer-calculation-modal"
      backdrop="static"
      keyboard={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t('CALCULATION_BREAKDOWN') || 'Calculation Breakdown'}
          {freelancerData.freelancerName && (
            <div className="freelancer-name">{freelancerData.freelancerName}</div>
          )}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body ref={bodyRef}>
        {/* Tab switcher: list breakdown vs. chart */}
        <div className={`fcm-tabs${indicator.width > 0 ? ' has-indicator' : ''}`} role="tablist" ref={tabsRef}>
          <span
            aria-hidden="true"
            className={`fcm-tab-indicator${indicatorReady ? ' is-animated' : ''}`}
            style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
          />
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'breakdown'}
            className={`fcm-tab${activeTab === 'breakdown' ? ' active' : ''}`}
            onClick={() => setActiveTab('breakdown')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            {t('BREAKDOWN') || 'Breakdown'}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'chart'}
            className={`fcm-tab${activeTab === 'chart' ? ' active' : ''}`}
            onClick={() => setActiveTab('chart')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 21H4a1 1 0 0 1-1-1V3" /><path d="M7 14l4-4 3 3 5-6" />
            </svg>
            {t('CHART') || 'Chart'}
          </button>
        </div>

        {(calcData.net < 0 || (calcData.setTotalAmount > 0 && calcData.net <= 0)) && (
          <div className="cbm-payout-warning" role="alert">
            {t('PAYOUT_NEGATIVE_ERROR')}
          </div>
        )}

        {activeTab === 'chart' && (
          <div className="fcm-chart-view">
            {composition.length > 0 && (
              <div className="fcm-chart-card">
                <h6 className="fcm-chart-title">{t('SALARY_DISTRIBUTION') || 'How the amount is distributed'}</h6>
                <div className="fcm-doughnut-wrap">
                  <div className="fcm-doughnut">
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                    <div className="fcm-doughnut-center">
                      <span className="fcm-center-label">{t('NET_SALARY') || 'Net Salary'}</span>
                      <span className="fcm-center-value">{formatCurrency(getAmount(calcData.net, calcData.netInSek || calcData.net))}</span>
                    </div>
                  </div>
                  <div className="fcm-legend">
                    {composition.map((item, i) => (
                      <div className="fcm-legend-row" key={item.label}>
                        <span className="fcm-legend-dot" style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }} />
                        <span className="fcm-legend-label">{item.label}</span>
                        <span className="fcm-legend-value">{formatCurrency(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {barSource.length > 0 && (
              <div className="fcm-chart-card">
                <h6 className="fcm-chart-title">{t('ALL_AMOUNTS') || 'All amounts'}</h6>
                <div className="fcm-bar" style={{ height: `${Math.max(220, barSource.length * 34)}px` }}>
                  <Bar data={barData} options={barOptions} />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="calculation-breakdown-single" style={{ display: activeTab === 'breakdown' ? undefined : 'none' }}>
          <div className="breakdown-section">
            {/* Step 1: Invoice amount inc. VAT */}
            <div className="calculation-item">
              <span className="item-label">{t('INVOICE_AMOUNT_INCLUDING_VAT') || 'Invoice Amount inc. VAT'}</span>
              <span className={valueCls(calcData.amountIncludingVat)}>{formatCurrencyOriginal(calcData.amountIncludingVat)}</span>
            </div>

            {/* Step 2: RUT/ROT Deduction (if applicable) */}
            {calcData.rutDeductionAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction'}</span>
                <span className="item-value">-{formatCurrency(calcData.rutDeductionAmount)}</span>
              </div>
            )}

            {calcData.rootDeductionAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction'}</span>
                <span className="item-value">-{formatCurrency(calcData.rootDeductionAmount)}</span>
              </div>
            )}

            {/* Step 3: Customer will pay (if RUT/ROT deduction exists) */}
            {(calcData.rutDeductionAmount > 0 || calcData.rootDeductionAmount > 0) && (
              <div className="calculation-item">
                <span className="item-label">{t('CUSTOMER_WILL_PAY') || 'Customer will pay'}</span>
                <span className={valueCls(calcData.amountIncludingVatAfterHouseholdServiceDeduction)}>{formatCurrency(calcData.amountIncludingVatAfterHouseholdServiceDeduction)}</span>
              </div>
            )}

            {/* Step 4: VAT */}
            {calcData.vat > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('VAT') || 'VAT'} ({calcData.vatPercentage}%)</span>
                <span className={valueCls(calcData.vat)}>{formatCurrencyOriginal(calcData.vat)}</span>
              </div>
            )}

            {/* Step 5: Invoice amount ex. VAT */}
            <div className="calculation-item">
              <span className="item-label">{t('INVOICE_AMOUNT_EXCLUDING_VAT') || 'Invoice amount ex. VAT'}</span>
              <span className={valueCls(getAmount(calcData.amount, calcData.amountInSek || calcData.amount))}>{formatCurrency(getAmount(calcData.amount, calcData.amountInSek || calcData.amount))}</span>
            </div>

            {/* Step 6: Invozio percent fee */}
            {calcData.fee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('INVOZIO_FEE') || 'Invozio percent fee'} ({calcData.fakturaFeePercentage}%)</span>
                <span className={valueCls(getAmount(calcData.fee, calcData.feeInSEK || calcData.fee))}>{formatCurrency(getAmount(calcData.fee, calcData.feeInSEK || calcData.fee))}</span>
              </div>
            )}

            {/* Step 7: Loyalty discount on invozio fee */}
            {calcData.loyaltyDiscountPercentage > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('LOYALTY_DISCOUNT_ON_INVOZIO_FEE') || 'Loyalty discount on invozio fee'}</span>
                <span className="item-value">{calcData.loyaltyDiscountPercentage}%</span>
              </div>
            )}

            {/* Step 8: Social Fee */}
            {calcData.socialFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">
                  {t('SOCIAL_FEE') || 'Social Fee'} ({getSocialFeeDisplayRate(calcData.socialFeeRateDeduction).toFixed(2)}%)
                </span>
                <span className={valueCls(getAmount(calcData.socialFee, calcData.socialFeeInSek || calcData.socialFee))}>{formatCurrency(getAmount(calcData.socialFee, calcData.socialFeeInSek || calcData.socialFee))}</span>
              </div>
            )}

            {/* Step 9: Pension Social Fee */}
            {calcData.pensionSocialFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('PENSION_SOCIAL_FEE') || 'Pension Social Fee'} ({calcData.pensionFeePer}%)</span>
                <span className={valueCls(getAmount(calcData.pensionSocialFee, calcData.pensionSocialFeeInSek || calcData.pensionSocialFee))}>{formatCurrency(getAmount(calcData.pensionSocialFee, calcData.pensionSocialFeeInSek || calcData.pensionSocialFee))}</span>
              </div>
            )}

            {/* Step 10: Pension */}
            {calcData.pension > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('PENSION') || 'Pension'}</span>
                <span className={valueCls(getAmount(calcData.pension, calcData.pensionInSek || calcData.pension))}>{formatCurrency(getAmount(calcData.pension, calcData.pensionInSek || calcData.pension))}</span>
              </div>
            )}

            {/* Step 11: Gross salary incl. holiday pay */}
            <div className="calculation-item">
              <span className="item-label">{t('GROSS_SALARY_INCLUDING_HOLIDAY_PAY') || 'Gross salary incl. holiday pay'}</span>
              <span className={valueCls(getAmount(calcData.gross, calcData.grossInSek || calcData.gross))}>{formatCurrency(getAmount(calcData.gross, calcData.grossInSek || calcData.gross))}</span>
            </div>

            {/* Step 12: Tax */}
            {calcData.tax > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('INCOME_TAX') || 'Tax'} ({calcData.taxPercentage || calcData.taxRateDeduction || '30.00'}%)</span>
                <span className={valueCls(getAmount(calcData.tax, calcData.taxInSek || calcData.tax))}>{formatCurrency(getAmount(calcData.tax, calcData.taxInSek || calcData.tax))}</span>
              </div>
            )}

            {/* Step 13: Holiday Pay */}
            {calcData.holidayPayAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('HOLIDAY_PAY') || 'Holiday Pay'}</span>
                <span className={valueCls(getAmount(calcData.holidayPayAmount, calcData.holidayPayAmountInSek || calcData.holidayPayAmount))}>{formatCurrency(getAmount(calcData.holidayPayAmount, calcData.holidayPayAmountInSek || calcData.holidayPayAmount))}</span>
              </div>
            )}

            {/* Step 14: Express Fee */}
            {calcData.expressFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPRESS_FEE') || 'Express Fee'}</span>
                <span className={valueCls(getAmount(calcData.expressFee, calcData.expressFeeInSek || calcData.expressFee))}>{formatCurrency(getAmount(calcData.expressFee, calcData.expressFeeInSek || calcData.expressFee))}</span>
              </div>
            )}

            {/* Step 15: Total Working Hours */}
            {/* {calcData.workingHours && calcData.workingHours.length > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('TOTAL_WORKING_HOURS') || 'Total Working Hours'}</span>
                <span className="item-value">{calcData.workingHours.reduce((sum: number, wh: any) => sum + (wh.workingHour || 0), 0)} {t('HOURS') || 'Hours'}</span>
              </div>
            )} */}

            {/* Step 16: Tax-free articles */}
            {calcData.mileageTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('MILEAGE_REIMBURSEMENT') || 'Mileage Reimbursement'}</span>
                <span className={valueCls(getAmount(calcData.mileageTaxFreeAmount, calcData.mileageTaxFreeAmountInSek || calcData.mileageTaxFreeAmount))}>{formatCurrency(getAmount(calcData.mileageTaxFreeAmount, calcData.mileageTaxFreeAmountInSek || calcData.mileageTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.expenditureForMaterialsAndToolsTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPENSES_FOR_MATERIALS_AND_TOOLS') || 'Material Tool & Expenses'}</span>
                <span className={valueCls(getAmount(calcData.expenditureForMaterialsAndToolsTaxFreeAmount, calcData.expenditureForMaterialsAndToolsTaxFreeAmountInSek || calcData.expenditureForMaterialsAndToolsTaxFreeAmount))}>{formatCurrency(getAmount(calcData.expenditureForMaterialsAndToolsTaxFreeAmount, calcData.expenditureForMaterialsAndToolsTaxFreeAmountInSek || calcData.expenditureForMaterialsAndToolsTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.allowanceAbroadTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden'}</span>
                <span className={valueCls(getAmount(calcData.allowanceAbroadTaxFreeAmount, calcData.allowanceAbroadTaxFreeAmountInSek || calcData.allowanceAbroadTaxFreeAmount))}>{formatCurrency(getAmount(calcData.allowanceAbroadTaxFreeAmount, calcData.allowanceAbroadTaxFreeAmountInSek || calcData.allowanceAbroadTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.domesticAllowanceFullDayTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('FULL_DAY') || 'Full Day'})</span>
                <span className={valueCls(getAmount(calcData.domesticAllowanceFullDayTaxFreeAmount, calcData.domesticAllowanceFullDayTaxFreeAmountInSek || calcData.domesticAllowanceFullDayTaxFreeAmount))}>{formatCurrency(getAmount(calcData.domesticAllowanceFullDayTaxFreeAmount, calcData.domesticAllowanceFullDayTaxFreeAmountInSek || calcData.domesticAllowanceFullDayTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.domesticAllowanceHalfDayTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden'} ({t('HALF_DAY') || 'Half Day'})</span>
                <span className={valueCls(getAmount(calcData.domesticAllowanceHalfDayTaxFreeAmount, calcData.domesticAllowanceHalfDayTaxFreeAmountInSek || calcData.domesticAllowanceHalfDayTaxFreeAmount))}>{formatCurrency(getAmount(calcData.domesticAllowanceHalfDayTaxFreeAmount, calcData.domesticAllowanceHalfDayTaxFreeAmountInSek || calcData.domesticAllowanceHalfDayTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.travelCostsAccordingTaxFreeAmount > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('TRAVEL_COSTS_ACCORDING_TO_ATTACHED_PDF') || 'Travel costs according to attached PDF'}</span>
                <span className={valueCls(getAmount(calcData.travelCostsAccordingTaxFreeAmount, calcData.travelCostsAccordingTaxFreeAmountInSek || calcData.travelCostsAccordingTaxFreeAmount))}>{formatCurrency(getAmount(calcData.travelCostsAccordingTaxFreeAmount, calcData.travelCostsAccordingTaxFreeAmountInSek || calcData.travelCostsAccordingTaxFreeAmount))}</span>
              </div>
            )}

            {calcData.allowance > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ALLOWANCE') || 'Other Allowances'}</span>
                <span className={valueCls(getAmount(calcData.allowance, calcData.allowanceInSek || calcData.allowance))}>{formatCurrency(getAmount(calcData.allowance, calcData.allowanceInSek || calcData.allowance))}</span>
              </div>
            )}

            {calcData.expense > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('EXPENSE') || 'Travel Expenses as per attached PDF'}</span>
                <span className={valueCls(getAmount(calcData.expense, calcData.expenseInSek || calcData.expense))}>{formatCurrency(getAmount(calcData.expense, calcData.expenseInSek || calcData.expense))}</span>
              </div>
            )}

            {calcData.administratorFee > 0 && (
              <div className="calculation-item">
                <span className="item-label">{t('ADMINISTRATOR_FEE') || 'Administrator Fee'}</span>
                <span className={valueCls(getAmount(calcData.administratorFee, calcData.administratorFeeInSek || calcData.administratorFee))}>{formatCurrency(getAmount(calcData.administratorFee, calcData.administratorFeeInSek || calcData.administratorFee))}</span>
              </div>
            )}
          </div>

          {/* Step 17: Net salary incl. holiday pay */}
          <div className={`breakdown-section net-salary-section${(calcData.net < 0 || (calcData.setTotalAmount > 0 && calcData.net <= 0)) ? ' payout-negative' : ''}`}>
            <div className="calculation-item">
              <span className="item-label item-label-bold">{t('NET_SALARY') || 'Net salary incl. holiday pay'}</span>
              <span className={valueCls(getAmount(calcData.net, calcData.netInSek || calcData.net))}>{formatCurrency(getAmount(calcData.net, calcData.netInSek || calcData.net))}</span>
            </div>
          </div>

          {/* Working Hours Table */}
          {calcData.workingHoursList && calcData.workingHoursList.length > 0 && (
            <div className="breakdown-section">
              <h6 className="section-title">{t('WORKING_HOURS_BREAKDOWN') || 'Working Hours Breakdown'}</h6>
              <div className="working-hours-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('ARTICLE') || 'Article'}</th>
                      <th>{t('HOURS') || 'Hours'}</th>
                      <th>{t('AMOUNT') || 'Amount'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calcData.workingHoursList.map((wh: any, index: number) => (
                      <tr key={index}>
                        <td>{wh.articleName || wh.articleDescription || '-'}</td>
                        <td>{wh.workingHour || wh.hours || '-'}</td>
                        <td>{formatCurrency(wh.amount || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          style={{
            backgroundColor: "#6750b8",
            borderRadius: "25px",
            boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.04)",
            display: "flex",
            padding: "10px 18px",
            justifyContent: "center",
            alignItems: "center",
            gap: "6px",
            alignSelf: "stretch",
            border: "none",
            WebkitTapHighlightColor: "transparent",
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden",
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none",
            touchAction: "manipulation"
          } as React.CSSProperties}
          onClick={onClose}
        >
          {t('CLOSE') || 'Close'}
        </Button>
      </Modal.Footer>

      {/* Custom scroll controls (replace the native scrollbar) */}
      <button
        type="button"
        aria-label={t('SCROLL_UP') || 'Scroll up'}
        className={`fcm-scroll-btn up${canScrollUp ? ' visible' : ''}`}
        onClick={() => scrollByViewport(-1)}
        tabIndex={canScrollUp ? 0 : -1}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 15l-6-6-6 6" />
        </svg>
      </button>
      <button
        type="button"
        aria-label={t('SCROLL_DOWN') || 'Scroll down'}
        className={`fcm-scroll-btn down${canScrollDown ? ' visible' : ''}`}
        onClick={() => scrollByViewport(1)}
        tabIndex={canScrollDown ? 0 : -1}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </Modal>
  );
};
